import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { debounce } from 'lodash'
import { useTheme } from '@ui-kitten/components'
import { AutocompleteDropdown, AutocompleteDropdownItem } from 'react-native-autocomplete-dropdown'
import { Storage } from '@utils'
import { Apollo, App } from '@config'
import { UserMin } from '@src/types'
import { Login } from '@src/models/app.types'
import { AllUsersAutocompleteQuery } from '@src/graphql/allUsersAutocomplete.generated'
import AutocompleteUserItem from './AutocompleteUserItem'

const SEARCH_DELAY = 200

interface AdminUserAutocompleteProps {
  placeholder: string
  onSelect: () => void
}

export const AdminUserAutocomplete = (props: AdminUserAutocompleteProps) => {
  const { placeholder = 'Search Name', onSelect: onSelectFromProps } = props

  const [users, setUsers] = useState<Pick<UserMin, 'id' | 'fullName' | 'email'>[]>([])

  const theme = useTheme()

  const suggestionsList = users.map((user) => ({ title: user.fullName, ...user }))

  const dispatch = useDispatch()

  const search = useCallback(
    (query: string) => {
      dispatch({
        type: 'users/autocomplete',
        payload: {
          query,
        },
        success: (response: AllUsersAutocompleteQuery['allUsersAutocomplete']) => {
          setUsers(response.users)
        },
        failure: () => {
          setUsers([])
        },
      })
    },
    [dispatch],
  )

  const debouncedSearch = useMemo(() => debounce(search, SEARCH_DELAY), [search])

  const onChangeText = useCallback(
    (query: string) => {
      debouncedSearch(query)
    },
    [debouncedSearch],
  )

  const onSelect = (id: string) => {
    onSelectFromProps()
    const toBeImpersonated = users.find((user) => user.id === id)

    if (!toBeImpersonated) {
      console.warn('No user found for impersonation:', id)
      return
    }

    const adminLogin = Storage.get<Login>('admin_login')
    const endpoint = Storage.get<string>('endpoint')

    if (!adminLogin?.token || !endpoint) {
      console.log('Impersonation Failed:')
      console.log('User: ', toBeImpersonated)
      console.log('Admin:', adminLogin)
      return
    }

    dispatch({
      type: 'app/logout',
      complete: () => {
        Storage.set('login', adminLogin)
        Storage.set('admin_login', adminLogin)
        Storage.set('endpoint', endpoint)

        console.log('Impersonating:')
        console.log('User: ', toBeImpersonated)
        console.log('Admin:', adminLogin.user)
        console.log('Endpoint:', endpoint)

        Apollo.resetEndpoint({ ...App.build, endpoint })

        dispatch({
          payload: {
            email: adminLogin.user?.email,
            password: '',
            impersonate: toBeImpersonated.email,
          },
          type: 'app/login',
        })
      },
    })
  }

  const renderItem = (item: AutocompleteDropdownItem) => {
    const user = (item as unknown) as UserMin
    return <AutocompleteUserItem email={user.email} fullName={user.fullName} />
  }

  return (
    <AutocompleteDropdown
      onChangeText={onChangeText}
      dataSet={suggestionsList}
      onSelectItem={(item) => {
        item && onSelect(item.id)
      }}
      closeOnBlur
      useFilter={false} // set false to prevent rerender twice
      showChevron={false}
      showClear={false}
      clearOnFocus={false}
      renderItem={renderItem}
      textInputProps={{
        placeholder,
        autoCorrect: false,
        autoCapitalize: 'none',
        style: {
          borderWidth: 2,
          borderRadius: 8,
          borderColor: theme['theme.surface.base'],
          backgroundColor: theme['theme.background'],
        },
      }}
    />
  )
}
